import { useState, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom";
import BottomNav from "./components/BottomNav";
import ErrorBoundary from "./components/ErrorBoundary";
import Paywall from "./components/Paywall";
import SaveProgressModal from "./components/SaveProgressModal";
import { PrivacyPolicy, TermsOfService, RefundPolicy, ContactPage } from "./components/PolicyPages";
import { SubscriptionProvider, useSubscription } from "./contexts/SubscriptionContext";
import { BookmarkCheck, RotateCcw } from "lucide-react";
import { PaymentNotice } from './components/PaymentNotice';

// ── Lazy-load each tab (code splitting — reduces initial bundle ~60%) ─────────
const DiceTab       = lazy(() => import("./components/DiceTab"));
const TruthOrDareTab = lazy(() => import("./components/TruthOrDareTab"));
const ShoppingTab   = lazy(() => import("./components/ShoppingTab"));
const GamesTab      = lazy(() => import("./components/GamesTab"));
const PositionsTab  = lazy(() => import("./components/PositionsTab"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));

const TABS = [DiceTab, TruthOrDareTab, ShoppingTab, GamesTab, PositionsTab];
const TAB_NAMES = ['dice', 'tod', 'shop', 'games', 'intimacy'];

// ── Tab loading skeleton ──────────────────────────────────────────────────────
function TabLoader() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
      <div className="flex gap-2">
        {[0, 150, 300].map(d => (
          <div key={d} className="w-3 h-3 rounded-full bg-[#FF8FAF] animate-bounce" style={{ animationDelay: `${d}ms` }} />
        ))}
      </div>
    </div>
  );
}

function MainApp() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = Math.max(0, TAB_NAMES.indexOf(searchParams.get('tab')));
  const setActiveTab = (index) => setSearchParams({ tab: TAB_NAMES[index] });
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showRestoreBanner, setShowRestoreBanner] = useState(
    () => !localStorage.getItem('lp_vid') && !localStorage.getItem('lp_restore_dismissed')
  );
  const ActiveComponent = TABS[activeTab];
  const { userEmail, isPremium } = useSubscription();

  const handleGoBackFromPositions = () => setActiveTab(0);

  const dismissRestore = () => {
    localStorage.setItem('lp_restore_dismissed', '1');
    setShowRestoreBanner(false);
  };

  const handleRestore = () => {
    setShowRestoreBanner(false);
    setShowSaveModal(true);
  };

  return (
    <div className="app-shell">

      {/* Returning user restore banner */}
      {showRestoreBanner && !userEmail && (
        <div data-testid="restore-progress-banner" className="relative shrink-0 z-50 bg-gradient-to-r from-[#FF8FAF] to-[#F4436C] px-3 py-2.5 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <RotateCcw size={14} color="white" />
            <p className="text-white text-xs font-semibold">Returning user? Restore your progress</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={handleRestore}
              data-testid="restore-progress-btn"
              className="bg-white text-[#F4436C] text-xs font-bold px-3 py-1 rounded-full active:scale-95 transition-all"
            >
              Restore
            </button>
            <button onClick={dismissRestore} data-testid="dismiss-restore-banner" aria-label="Dismiss restore reminder" className="text-white/80 text-lg leading-none">×</button>
          </div>
        </div>
      )}

      {!isPremium && !userEmail && !showRestoreBanner && (
        <div className="relative shrink-0 flex justify-end px-4 py-2 bg-white/60" data-testid="progress-toolbar">
          <button onClick={() => setShowSaveModal(true)} data-testid="floating-save-progress-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#FFE0E8] text-[10px] font-semibold text-[#AB3657] hover:bg-[#FFF0F3] transition-colors">
            <BookmarkCheck size={12} /> Save Progress
          </button>
        </div>
      )}
      <PaymentNotice />
      <main className="app-content" data-testid="app-content" key={activeTab}>
        <ErrorBoundary>
          <Suspense fallback={<TabLoader />}>
            {activeTab === 4 ? (
              <PositionsTab onGoBack={handleGoBackFromPositions} />
            ) : (
              <ActiveComponent />
            )}
          </Suspense>
        </ErrorBoundary>
      </main>
      <BottomNav activeTab={activeTab} onTabChange={setActiveTab} />
      <Paywall />
      {showSaveModal && <SaveProgressModal onClose={() => setShowSaveModal(false)} />}

    </div>
  );
}

function App() {
  return (
    <SubscriptionProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <Suspense fallback={<TabLoader />}>
            <Routes>
              <Route path="/admin"   element={<AdminDashboard />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms"   element={<TermsOfService />} />
              <Route path="/refund"  element={<RefundPolicy />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="*"        element={<MainApp />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </SubscriptionProvider>
  );
}

export default App;
